<template>
  <div v-if="range" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedRange?.name"
      link-name="ranges-management"
      :is-modified="isModified"
      :mode="mode"
      :is-edit-active="range.isFromManager"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerContent>
        <template v-if="isHorizontalLayout">
          <RangeDetails
            v-model="modifiedRange"
            :range="range"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout">
          </RangeDetails>
        </template>
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2">
            <RangeDetails
              v-model="modifiedRange"
              :range="range"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
            </RangeDetails>
          </PerfectScrollbar>
        </template>
      </template>

      <template #detailsTitle>
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer capitalize"
              :class="selectedTab === tab.name ? 'border-b-2 border-theme-500' : ''">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>
      <template #detailsContent>
        <template v-if="selectedTab === 'materials'">
          <RangeItemsTable
            :table-data="rangeItemsTableData"
            :all-modified-data="modifiedRangeItems"
            :table-data-count="rangeItemsTableDataTotalCount"
            :selected-page="rangeItemsSelectedPage"
            :mode="mode"
            :range-id="String(route.params.id)"
            :addedDetails="addedModifiedDetails"
            @refresh-table-data="loadRangeItems"
            @add="onAddMaterials"
            @delete="onDeleteRangeItems"
            @update="onUpdateRangeItems" />
        </template>
        <template v-else>
          <RangeTradersitesTable
            :table-data="rangeCustomerTradersitesTableData"
            :table-data-count="rangeCustomerTradersitesTableDataTotalCount"
            :selected-page="rangeCustomerTradersitesSelectedPage"
            :items-per-page="rangeCustomerTradersitesPerPage"
            :mode="mode"
            :range-id="String(route.params.id)"
            :addedDetails="addedModifiedCustomerTradersites"
            @refresh-table-data="loadRangeCustomerTradersites"
            @add="onAddRangeTradersiteCustomers"
            @delete="onDeleteRangeTradersiteCustomers">
          </RangeTradersitesTable>
        </template>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { deepCopy } from '@/utils/data';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import RangeDetails from '@/components/ranges/RangeDetails.vue';
import RangeItemsTable from '@/components/ranges/RangeItemsTable.vue';
import RangeTradersitesTable from '@/components/ranges/RangeTradersitesTable.vue';
import {
  getRangeItemsWithConditions,
  getRangeCustomerTradersitesWithConditions,
  getRangeById,
  addEditRangeItems,
  addEditRange,
  addEditRangeCustomerTradersites,
} from '@/services/api/ranges';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { Range, RangeCustomerTradersite, RangeItem } from '@/types/Ranges';
import { useI18n } from 'vue-i18n';

const isModified = ref<boolean>(false);
const { t: $t } = useI18n();
const $v = useVuelidate();
const route = useRoute();
const router = useRouter();
const range = ref<Range>();
const modifiedRange = ref<Range>();
const modifiedRangeItems = ref<RangeItem[]>([]);
const modifiedCustomerTradersites = ref<RangeCustomerTradersite[]>([]);
const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const confirmModalActive = ref(false);
const isBackMode = ref(false);

watchEffect(() => {
  isModified.value =
    JSON.stringify(modifiedRange.value) !== JSON.stringify(range.value) ||
    modifiedRangeItems.value.length > 0 ||
    modifiedCustomerTradersites.value.length > 0;
});

onMounted(async () => {
  await loadData();
});

async function loadData(preserveCurrentPages = false) {
  const res = await getRangeById(route.params.id as string);
  if (res) {
    range.value = res as Range;
    mode.value = 'view';
  } else {
    range.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      name: '',
      code: '',
    };
    mode.value = 'edit';
  }
  if (range.value) modifiedRange.value = deepCopy<Range>(range.value);

  modifiedRangeItems.value = [];
  modifiedCustomerTradersites.value = [];

  const rangeItemsPage = preserveCurrentPages ? rangeItemsSelectedPage.value : 1;
  const customerTradersitesPage = preserveCurrentPages
    ? rangeCustomerTradersitesSelectedPage.value
    : 1;

  await loadRangeItems({
    page: rangeItemsPage,
    itemsPerPage: rangeItemsPerPage.value,
    rangeId: String(route.params.id),
  });
  if (!preserveCurrentPages) rangeItemsSelectedPage.value = 1;

  await loadRangeCustomerTradersites({
    page: customerTradersitesPage,
    itemsPerPage: rangeCustomerTradersitesPerPage.value,
    rangeId: String(route.params.id),
  });
  if (!preserveCurrentPages) rangeCustomerTradersitesSelectedPage.value = 1;
}

const rangeItemsTableData = ref<RangeItem[]>([]);
const rangeItemsTableDataTotalCount = ref<number>(0);
const rangeItemsSelectedPage = ref(1);
const rangeItemsPerPage = ref(10);

async function loadRangeItems(data: TableFilterData & { rangeId: string }) {
  if (!modifiedRange.value) return;
  const currentPage = data.page ?? 1;
  rangeItemsPerPage.value = data.itemsPerPage ?? 10;

  let addedItems: RangeItem[] = [];
  if (modifiedRangeItems.value)
    addedItems = modifiedRangeItems.value.filter((x) => x.recordMode == RecordMode.Added);

  let serverItems: RangeItem[] = [];
  let serverItemsCount: number = 0;
  const res = await getRangeItemsWithConditions({ ...data });
  if (res) {
    serverItems = res.data;
    serverItemsCount = res.count;
  }
  const serverPages = Math.ceil(serverItemsCount / rangeItemsPerPage.value);
  const currentPageItemCount = currentPage <= serverPages ? serverItems.length : 0;
  const pageAvailableSpace = Math.max(0, rangeItemsPerPage.value - currentPageItemCount);

  if (currentPage < serverPages)
    // get page fromServer
    rangeItemsTableData.value = serverItems;
  else if (currentPage > serverPages) {
    // get page from local data
    const mod = serverItemsCount % rangeItemsPerPage.value;
    const availableSpace = mod === 0 ? 0 : rangeItemsPerPage.value - mod;
    const modifiedCurrentPage = currentPage - serverPages;
    const startIndex = (modifiedCurrentPage - 1) * rangeItemsPerPage.value + availableSpace;
    const endIndex = startIndex + rangeItemsPerPage.value;
    rangeItemsTableData.value = addedItems.slice(startIndex, endIndex);
  } else {
    // get boundary page (last server page + fill with modified)
    if (pageAvailableSpace > 0) {
      const modifiedData = addedItems.slice(0, pageAvailableSpace);
      rangeItemsTableData.value = [...serverItems, ...modifiedData];
    } else rangeItemsTableData.value = serverItems;
  }

  rangeItemsTableDataTotalCount.value = serverItemsCount + addedItems.length;

  // Filter deleted from TableData
  let deletedDetails: RangeItem[] = [];
  if (modifiedRangeItems.value)
    deletedDetails = modifiedRangeItems.value.filter((x) => x.recordMode == RecordMode.Deleted);

  if (deletedDetails.length)
    deletedDetails.forEach((deleted) => {
      const existing = rangeItemsTableData.value.find((x) => x.id === deleted.id);
      if (existing) existing.deleted = true;
    });

  // Update detail that have modified values
  let updatedDetails: RangeItem[] = [];
  if (modifiedRangeItems.value)
    updatedDetails = modifiedRangeItems.value.filter((x) => x.recordMode == RecordMode.Modified);

  if (updatedDetails.length) {
    updatedDetails.forEach((detail) => {
      const existingIndex = rangeItemsTableData.value.findIndex((x) => x.id === detail.id);
      if (existingIndex !== -1) rangeItemsTableData.value[existingIndex] = detail;
    });
  }
}

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') isBackMode.value = true;
    return;
  } else if (!range.value?.createdDate) router.push('/ranges-management');
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (range.value) modifiedRange.value = deepCopy<Range>(range.value);
  if (!range.value?.createdDate || isBackMode.value) router.push('/ranges-management');
  else await loadData();
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (isModified.value) {
    if (modifiedRange.value) await addEditRange(modifiedRange.value);
    await addEditRangeItems(modifiedRangeItems.value);
    await addEditRangeCustomerTradersites(modifiedCustomerTradersites.value);

    // Refresh data from server while preserving current page positions
    await loadData(true);
  }
  mode.value = 'view';
}

const addedModifiedDetails = computed(() =>
  modifiedRangeItems.value.filter((x) => x.recordMode === RecordMode.Added)
);

async function onAddMaterials(details: RangeItem[]) {
  modifiedRangeItems.value.push(...details);

  // Calculate the new total count including the newly added items
  const newTotalCount = rangeItemsTableDataTotalCount.value + details.length;
  const lastPage = Math.ceil(newTotalCount / rangeItemsPerPage.value);

  await loadRangeItems({
    page: lastPage,
    itemsPerPage: rangeItemsPerPage.value,
    rangeId: String(route.params.id),
  });
  rangeItemsSelectedPage.value = lastPage;
}

function onDeleteRangeItems(details: RangeItem[]) {
  details.forEach((detail) => {
    const existingIndex = modifiedRangeItems.value.findIndex((x) => x.id === detail.id);
    if (existingIndex !== -1) {
      const existingDetail = modifiedRangeItems.value[existingIndex];
      if (existingDetail.recordMode === RecordMode.Added) {
        modifiedRangeItems.value.splice(existingIndex, 1);
        const existingIndexInTable = rangeItemsTableData.value.findIndex((x) => x.id === detail.id);
        if (existingIndexInTable !== -1) rangeItemsTableData.value.splice(existingIndexInTable, 1);
      } else if (existingDetail.recordMode === RecordMode.Modified) {
        existingDetail.recordMode = RecordMode.Deleted;
        const existingIndexInTable = rangeItemsTableData.value.find((x) => x.id === detail.id);
        if (existingIndexInTable) existingIndexInTable.deleted = true;
      }
    } else {
      const existing = rangeItemsTableData.value.find((x) => x.id === detail.id);
      if (existing) existing.deleted = true;
      detail.recordMode = RecordMode.Deleted;
      modifiedRangeItems.value.push(detail);
    }
  });
}

function onUpdateRangeItems(detail: RangeItem) {
  const existingIndex = modifiedRangeItems.value.findIndex((x) => x.id === detail.id);
  if (existingIndex !== -1) modifiedRangeItems.value.splice(existingIndex, 1);
  modifiedRangeItems.value.push(detail);

  const existingInTable = rangeItemsTableData.value.findIndex((x) => x.id === detail.id);
  if (existingInTable !== -1) rangeItemsTableData.value[existingInTable] = detail;
}

const tabs = ref([
  {
    name: 'materials',
    label: $t('material.materials'),
  },
  {
    name: 'traders',
    label: $t('trader.traders'),
  },
]);

const selectedTab = ref('materials');

const rangeCustomerTradersitesTableData = ref<RangeCustomerTradersite[]>([]);
const rangeCustomerTradersitesTableDataTotalCount = ref<number>(0);
const rangeCustomerTradersitesSelectedPage = ref(1);
const rangeCustomerTradersitesPerPage = ref(10);

const addedModifiedCustomerTradersites = computed(() =>
  modifiedCustomerTradersites.value.filter((x) => x.recordMode === RecordMode.Added)
);

async function loadRangeCustomerTradersites(data: TableFilterData & { rangeId: string }) {
  if (!modifiedRange.value) return;
  const currentPage = data.page ?? 1;
  rangeCustomerTradersitesPerPage.value = data.itemsPerPage ?? 10;

  let addedItems: RangeCustomerTradersite[] = [];
  if (modifiedCustomerTradersites.value)
    addedItems = modifiedCustomerTradersites.value.filter((x) => x.recordMode == RecordMode.Added);

  let serverItems: RangeCustomerTradersite[] = [];
  let serverItemsCount: number = 0;
  const res = await getRangeCustomerTradersitesWithConditions({ ...data });
  if (res) {
    serverItems = res.data;
    serverItemsCount = res.count;
  }
  const serverPages = Math.ceil(serverItemsCount / rangeCustomerTradersitesPerPage.value);
  const currentPageItemCount = currentPage <= serverPages ? serverItems.length : 0;
  const pageAvailableSpace = Math.max(
    0,
    rangeCustomerTradersitesPerPage.value - currentPageItemCount
  );

  if (currentPage < serverPages)
    // get page fromServer
    rangeCustomerTradersitesTableData.value = serverItems;
  else if (currentPage > serverPages) {
    // get page from local data
    const mod = serverItemsCount % rangeCustomerTradersitesPerPage.value;
    const availableSpace = mod === 0 ? 0 : rangeCustomerTradersitesPerPage.value - mod;
    const modifiedCurrentPage = currentPage - serverPages;
    const startIndex =
      (modifiedCurrentPage - 1) * rangeCustomerTradersitesPerPage.value + availableSpace;
    const endIndex = startIndex + rangeCustomerTradersitesPerPage.value;
    rangeCustomerTradersitesTableData.value = addedItems.slice(startIndex, endIndex);
  } else {
    // get boundary page (last server page + fill with modified)
    if (pageAvailableSpace > 0) {
      const modifiedData = addedItems.slice(0, pageAvailableSpace);
      rangeCustomerTradersitesTableData.value = [...serverItems, ...modifiedData];
    } else rangeCustomerTradersitesTableData.value = serverItems;
  }

  rangeCustomerTradersitesTableDataTotalCount.value = serverItemsCount + addedItems.length;
}

async function onAddRangeTradersiteCustomers(items: RangeCustomerTradersite[]) {
  modifiedCustomerTradersites.value.push(...items);

  // Calculate the new total count including the newly added items
  const newTotalCount = rangeCustomerTradersitesTableDataTotalCount.value + items.length;
  const lastPage = Math.ceil(newTotalCount / rangeCustomerTradersitesPerPage.value);

  // Navigate to last page and refresh table data to show the newly added items
  await loadRangeCustomerTradersites({
    page: lastPage,
    itemsPerPage: rangeCustomerTradersitesPerPage.value,
    rangeId: String(route.params.id),
  });
  rangeCustomerTradersitesSelectedPage.value = lastPage;
}

function onDeleteRangeTradersiteCustomers(items: RangeCustomerTradersite[]) {
  items.forEach((item) => {
    const existingIndex = modifiedCustomerTradersites.value.findIndex((x) => x.id === item.id);
    if (existingIndex !== -1) {
      const existingItem = modifiedCustomerTradersites.value[existingIndex];
      if (existingItem.recordMode === RecordMode.Added) {
        modifiedCustomerTradersites.value.splice(existingIndex, 1);
        const existingIndexInTable = rangeCustomerTradersitesTableData.value.findIndex(
          (x) => x.id === item.id
        );
        if (existingIndexInTable !== -1)
          rangeCustomerTradersitesTableData.value.splice(existingIndexInTable, 1);
      } else if (existingItem.recordMode === RecordMode.Modified) {
        existingItem.recordMode = RecordMode.Deleted;
        const existingIndexInTable = rangeCustomerTradersitesTableData.value.find(
          (x) => x.id === item.id
        );
        if (existingIndexInTable) existingIndexInTable.deleted = true;
      }
    } else {
      const existing = rangeCustomerTradersitesTableData.value.find((x) => x.id === item.id);
      if (existing) existing.deleted = true;
      item.recordMode = RecordMode.Deleted;
      modifiedCustomerTradersites.value.push(item);
    }
  });
}
</script>
